import { Fragment, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChartCard } from "@/components/ui/chart-card";
import { OrgBillLine } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import {
  PRICING_CLASS_HINTS,
  PRICING_CLASS_LABELS,
  countryName,
  formatUnitPrice,
  formatUsd,
  labelOf
} from "../org-kpis.meta";

type Rules = "new" | "then";

const RULES: { key: Rules; label: string; description: string }[] = [
  {
    key: "new",
    label: "New pricing",
    description: "Every message priced under the 1 Oct 2026 rules at today's rates."
  },
  {
    key: "then",
    label: "At the time",
    description: "Every message priced under the rules and rates of the day it was sent."
  }
];

interface BillCardProps {
  bill: OrgBillLine[];
}

/** The estimated Meta bill: kind of message × customer country, priced. */
export function BillCard({ bill }: BillCardProps) {
  const [rules, setRules] = useState<Rules>("new");

  const groups = useMemo(() => {
    const byClass = new Map<string, OrgBillLine[]>();
    for (const line of bill) {
      byClass.set(line.pricingClass, [...(byClass.get(line.pricingClass) ?? []), line]);
    }
    return [...byClass.entries()]
      .map(([pricingClass, lines]) => ({
        pricingClass,
        lines: [...lines].sort((a, b) => b[rules] - a[rules] || b.messages - a.messages),
        messages: lines.reduce((n, l) => n + l.messages, 0),
        freeTier: lines.reduce((n, l) => n + l.freeTier, 0),
        total: lines.reduce((n, l) => n + l[rules], 0)
      }))
      .sort((a, b) => b.total - a.total || b.messages - a.messages);
  }, [bill, rules]);

  const total = groups.reduce((n, g) => n + g.total, 0);
  const messages = groups.reduce((n, g) => n + g.messages, 0);

  const unitOf = (line: OrgBillLine) => {
    const unit = rules === "new" ? line.newUnit : line.thenUnit;
    if (unit !== null) return formatUnitPrice(unit);
    if (rules === "new" || line.messages === 0) return "no rate";
    // The period crossed a rate or rule change: show what one cost on average.
    return `avg ${formatUnitPrice(line.then / line.messages)}`;
  };

  return (
    <ChartCard
      title="Estimated bill"
      description={`${RULES.find((r) => r.key === rules)!.description} Meta prices by the customer's country.`}
      action={
        <div className="flex gap-1">
          {RULES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={rules === r.key ? "default" : "outline"}
              onClick={() => setRules(r.key)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      }
    >
      {groups.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nothing billable was delivered in this period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 font-medium">Message type · country</th>
                <th className="py-2 text-right font-medium">Price per message</th>
                <th className="py-2 text-right font-medium">Messages</th>
                <th className="py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.pricingClass}>
                  <tr className="border-b bg-muted/40 align-top">
                    <td className="px-2 py-2" colSpan={2}>
                      <div className="font-medium">
                        {labelOf(PRICING_CLASS_LABELS, group.pricingClass)}
                      </div>
                      {PRICING_CLASS_HINTS[group.pricingClass] && (
                        <div className="text-xs text-muted-foreground">
                          {PRICING_CLASS_HINTS[group.pricingClass]}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums">
                      {formatters.formatNumber(group.messages)}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums">
                      {formatUsd(group.total)}
                    </td>
                  </tr>
                  {group.lines.map((line) => (
                    <tr
                      key={`${line.pricingClass}-${line.country}`}
                      className="border-b align-top last:border-0"
                    >
                      <td className="py-2 pl-6">{countryName(line.country)}</td>
                      <td className="py-2 text-right tabular-nums text-muted-foreground">
                        {unitOf(line)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatters.formatNumber(line.messages)}
                        {line.freeTier > 0 && (
                          <div className="text-xs text-muted-foreground">
                            + {formatters.formatNumber(line.freeTier)} in the free tier
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatUsd(line[rules])}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2">
                <td className="py-3 font-semibold" colSpan={2}>
                  Total
                </td>
                <td className="py-3 text-right font-semibold tabular-nums">
                  {formatters.formatNumber(messages)}
                </td>
                <td className="px-2 py-3 text-right text-base font-bold tabular-nums">
                  {formatUsd(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </ChartCard>
  );
}
